import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('stilos_token'));

  const getAuthHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const [userRes, subRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers: getAuthHeaders() }),
        axios.get(`${API}/subscription`, { headers: getAuthHeaders() })
      ]);
      setUser(userRes.data);
      setSubscription(subRes.data);
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('stilos_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token, getAuthHeaders]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('stilos_token', access_token);
    setToken(access_token);
    setUser(userData);
    await fetchUser();
    return userData;
  };

  const register = async (email, password, confirmPassword, fullName, companyName) => {
    // Registration no longer returns a session — the account is unconfirmed. The
    // caller shows a "check your email" state; the user logs in after confirming.
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      confirm_password: confirmPassword,
      full_name: fullName,
      company_name: companyName
    });
    return response.data; // { message, email, requires_confirmation }
  };

  const logout = () => {
    localStorage.removeItem('stilos_token');
    setToken(null);
    setUser(null);
    setSubscription(null);
  };

  const refreshSubscription = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/subscription`, { headers: getAuthHeaders() });
      setSubscription(res.data);
    } catch (error) {
      console.error('Failed to refresh subscription:', error);
    }
  };

  const value = {
    user,
    subscription,
    loading,
    token,
    login,
    register,
    logout,
    getAuthHeaders,
    refreshSubscription,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
